import { useState } from 'react'
import { useRouter } from '../../lib/router'
import { MarketingLayout } from './MarketingLayout'
import { getAttribution, track } from '../../lib/analytics'
import { useIsMobile } from '../../lib/useIsMobile'

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:5000'
const CONTACT_NUMBER_DISPLAY = '064 900 0805'
const CONTACT_NUMBER_INTERNATIONAL = '+27649000805'
const WHATSAPP_LINK = 'https://wa.me/27649000805'
const CALL_LINK = `tel:${CONTACT_NUMBER_INTERNATIONAL}`


type FormState = 'idle' | 'submitting' | 'success' | 'error'

export function ContactPage() {
  const { search } = useRouter()
  const intent = search.get('intent')
  const isMobile = useIsMobile()

  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [workshopName, setWorkshopName] = useState('')
  const [bays, setBays] = useState('')
  const [message, setMessage] = useState(() => {
    if (intent === 'whatsapp') return 'I want to see the WhatsApp quote approval and shared inbox workflow.'
    if (intent === 'dvi') return 'I want to see the mobile DVI and inspection media workflow.'
    if (intent === 'payments') return 'I want to see invoice payment links and webhook reconciliation.'
    if (intent === 'accounting') return 'I want to discuss Xero, Sage or Zoho accounting sync.'
    if (intent === 'workshop') return 'I want a full workshop management demo.'
    return ''
  })
  const [formState, setFormState] = useState<FormState>('idle')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setFormState('submitting')
    track('Marketing Lead Attempt', { intent })
    try {
      const res = await fetch(`${API_URL}/v1/marketing/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          phone,
          email,
          workshopName,
          bays,
          message,
          intent,
          ...getAttribution(),
        }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      setFormState('success')
      track('Marketing Lead Submitted', { intent, bays })
    } catch {
      setFormState('error')
      track('Marketing Lead Failed', { intent })
    }
  }

  return (
    <MarketingLayout>
      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section style={{
        background: 'var(--navy-900, #0d1b2e)',
        padding: '80px var(--space-6) 64px',
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: 640, margin: '0 auto' }}>
          <h1 style={{
            fontFamily: 'var(--font-display)', fontSize: 'clamp(32px, 5vw, 52px)',
            fontWeight: 700, color: '#fff', letterSpacing: '-0.03em',
            lineHeight: 1.1, marginBottom: 20,
          }}>Let's talk workshops</h1>
          <p style={{ fontSize: 18, color: 'var(--neutral-400)', lineHeight: 1.6 }}>
            Book a 20-minute walkthrough, ask a question, or claim your 30-day free pilot. A human in Johannesburg will respond within 4 hours during business hours.
          </p>
        </div>
      </section>

      {/* ── Contact grid ──────────────────────────────────────────────────── */}
      <section style={{ padding: '80px var(--space-6)', background: '#fff' }}>
        <div style={{
          maxWidth: 1040, margin: '0 auto',
          display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1.4fr', gap: isMobile ? 32 : 64, alignItems: 'flex-start',
        }}>
          {/* Left col — contact info */}
          <div>
            <h2 style={{
              fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700,
              color: 'var(--navy-700)', marginBottom: 8,
            }}>Contact details</h2>
            <p style={{ fontSize: 14, color: 'var(--fg-3)', marginBottom: 36, lineHeight: 1.6 }}>
              SA business hours: Mon–Fri 08:00–17:00, Sat 08:00–13:00 (SAST). Emergency support for active subscribers outside these hours.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {[
                {
                  icon: (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#25D366" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                  ),
                  label: 'WhatsApp',
                  value: CONTACT_NUMBER_DISPLAY,
                  href: WHATSAPP_LINK,
                  note: 'Fastest response — most questions answered same day',
                },
                {
                  icon: (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--navy-700)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  ),
                  label: 'Email',
                  value: 'hello@autoaxxiz.co.za',
                  note: 'For detailed questions or formal enquiries',
                },
                {
                  icon: (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--navy-700)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13 19.79 19.79 0 0 1 1.61 4.37 2 2 0 0 1 3.6 2.18h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l.85-.85a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 17z" />
                    </svg>
                  ),
                  label: 'Call',
                  value: CONTACT_NUMBER_DISPLAY,
                  href: CALL_LINK,
                  note: 'Mon–Fri 08:00–17:00 SAST',
                },
                {
                  icon: (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--navy-700)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  ),
                  label: 'Office',
                  value: 'Johannesburg, Gauteng',
                  note: 'Visits by appointment only',
                },
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 10,
                    background: 'var(--bg-2, #f8f9fa)',
                    border: '1px solid var(--border-subtle)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {item.icon}
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: 'var(--fg-4)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>{item.label}</div>
                    {item.href ? (
                      <a
                        href={item.href}
                        target={item.href.startsWith('http') ? '_blank' : undefined}
                        rel={item.href.startsWith('http') ? 'noreferrer' : undefined}
                        style={{ display: 'inline-block', fontSize: 15, fontWeight: 600, color: 'var(--navy-700)', marginBottom: 2, textDecoration: 'none' }}
                      >
                        {item.value}
                      </a>
                    ) : (
                      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--navy-700)', marginBottom: 2 }}>{item.value}</div>
                    )}
                    <div style={{ fontSize: 13, color: 'var(--fg-3)' }}>{item.note}</div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{
              marginTop: 36, padding: '20px', background: 'var(--bg-2, #f8f9fa)',
              borderRadius: 12, border: '1px solid var(--border-subtle)',
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy-700)', marginBottom: 8 }}>30-day free pilot includes:</div>
              <ul style={{ margin: 0, padding: '0 0 0 16px', fontSize: 13, color: 'var(--fg-2)', lineHeight: 1.9 }}>
                <li>Full workshop onboarding</li>
                <li>Customer and vehicle data import</li>
                <li>WhatsApp Business number setup</li>
                <li>Staff training (up to 2 hours)</li>
                <li>No credit card required</li>
              </ul>
            </div>
          </div>

          {/* Right col — form */}
          <div>
            {formState === 'success' ? (
              <div style={{
                background: 'var(--bg-2, #f8f9fa)', border: '1px solid var(--border-subtle)',
                borderRadius: 16, padding: '48px 40px', textAlign: 'center',
              }}>
                <div style={{
                  width: 56, height: 56, borderRadius: 14,
                  background: 'rgba(34,197,94,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 20px',
                }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h3 style={{
                  fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700,
                  color: 'var(--navy-700)', marginBottom: 12,
                }}>Message received</h3>
                <p style={{ fontSize: 15, color: 'var(--fg-2)', lineHeight: 1.7 }}>
                  We'll be in touch within 4 business hours. If you need us sooner, WhatsApp us directly.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                style={{
                  background: 'var(--bg-2, #f8f9fa)', border: '1px solid var(--border-subtle)',
                  borderRadius: 16, padding: '40px 36px',
                }}
              >
                <h2 style={{
                  fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700,
                  color: 'var(--navy-700)', marginBottom: 28,
                }}>Tell us about your workshop</h2>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 16 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--fg-2)', marginBottom: 6 }}>
                      Your name <span style={{ color: 'var(--orange-500,#D96E1A)' }}>*</span>
                    </label>
                    <input
                      required value={name} onChange={e => setName(e.target.value)}
                      placeholder="Themba Ndlovu"
                      style={{
                        width: '100%', padding: '10px 14px', borderRadius: 8,
                        border: '1px solid var(--border-strong)', background: '#fff',
                        fontSize: 14, color: 'var(--fg-1)', boxSizing: 'border-box',
                        outline: 'none',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--fg-2)', marginBottom: 6 }}>
                      Cell / WhatsApp <span style={{ color: 'var(--orange-500,#D96E1A)' }}>*</span>
                    </label>
                    <input
                      required value={phone} onChange={e => setPhone(e.target.value)}
                      placeholder="+27 82 000 0000"
                      type="tel"
                      style={{
                        width: '100%', padding: '10px 14px', borderRadius: 8,
                        border: '1px solid var(--border-strong)', background: '#fff',
                        fontSize: 14, color: 'var(--fg-1)', boxSizing: 'border-box',
                        outline: 'none',
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--fg-2)', marginBottom: 6 }}>
                    Email address
                  </label>
                  <input
                    value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="themba@workshopname.co.za"
                    type="email"
                    style={{
                      width: '100%', padding: '10px 14px', borderRadius: 8,
                      border: '1px solid var(--border-strong)', background: '#fff',
                      fontSize: 14, color: 'var(--fg-1)', boxSizing: 'border-box',
                      outline: 'none',
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 16 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--fg-2)', marginBottom: 6 }}>
                      Workshop name <span style={{ color: 'var(--orange-500,#D96E1A)' }}>*</span>
                    </label>
                    <input
                      required value={workshopName} onChange={e => setWorkshopName(e.target.value)}
                      placeholder="Ndlovu Auto"
                      style={{
                        width: '100%', padding: '10px 14px', borderRadius: 8,
                        border: '1px solid var(--border-strong)', background: '#fff',
                        fontSize: 14, color: 'var(--fg-1)', boxSizing: 'border-box',
                        outline: 'none',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--fg-2)', marginBottom: 6 }}>
                      Number of bays
                    </label>
                    <select
                      value={bays} onChange={e => setBays(e.target.value)}
                      style={{
                        width: '100%', padding: '10px 14px', borderRadius: 8,
                        border: '1px solid var(--border-strong)', background: '#fff',
                        fontSize: 14, color: bays ? 'var(--fg-1)' : 'var(--fg-4)',
                        boxSizing: 'border-box', outline: 'none',
                      }}
                    >
                      <option value="">Select...</option>
                      <option value="1">1 bay</option>
                      <option value="2-3">2–3 bays</option>
                      <option value="4-5">4–5 bays</option>
                      <option value="6+">6+ bays</option>
                      <option value="multi">Multiple locations</option>
                    </select>
                  </div>
                </div>

                <div style={{ marginBottom: 24 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--fg-2)', marginBottom: 6 }}>
                    What would you like to talk about?
                  </label>
                  <textarea
                    value={message} onChange={e => setMessage(e.target.value)}
                    rows={4}
                    placeholder="Book a demo, ask about pricing, or describe what you're looking to fix in your workshop..."
                    style={{
                      width: '100%', padding: '10px 14px', borderRadius: 8,
                      border: '1px solid var(--border-strong)', background: '#fff',
                      fontSize: 14, color: 'var(--fg-1)', boxSizing: 'border-box',
                      outline: 'none', resize: 'vertical', fontFamily: 'inherit',
                    }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={formState === 'submitting'}
                  style={{
                    width: '100%', padding: '13px 0', borderRadius: 8,
                    fontSize: 15, fontWeight: 600, cursor: formState === 'submitting' ? 'wait' : 'pointer',
                    background: 'var(--orange-500, #D96E1A)', border: 'none', color: '#fff',
                    opacity: formState === 'submitting' ? 0.7 : 1,
                  }}
                >
                  {formState === 'submitting' ? 'Sending...' : 'Send message'}
                </button>
                {formState === 'error' && (
                  <p style={{ fontSize: 13, color: 'var(--danger-600, #dc2626)', textAlign: 'center', marginTop: 12 }}>
                    We could not send that. Please WhatsApp or email us directly.
                  </p>
                )}
                <p style={{ fontSize: 12, color: 'var(--fg-4)', textAlign: 'center', marginTop: 12 }}>
                  We'll respond within 4 business hours. No spam, ever.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </MarketingLayout>
  )
}
